import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Lock as PrivacyIcon,
  VpnKey as KeyIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { getUserData, getAuthToken } from '../utils/authUtils';
import { getApiBaseUrl } from '../utils/apiConfig';
import axios from 'axios';

const AccountSettings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Notification preferences
  const [notifications, setNotifications] = useState({
    pushNotifications: true,
    emailUpdates: true,
    invitationAlerts: true,
    messageAlerts: true,
    marketingEmails: false,
    weeklyDigest: true
  });

  // Privacy settings
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'public', // public, private, connections
    showEmail: false,
    showLinkedIn: true,
    allowDirectMessages: true,
    showOnlineStatus: true
  });

  // Account deletion dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Load user data and settings on mount
  useEffect(() => {
    loadAccountSettings();
  }, []);

  const loadAccountSettings = useCallback(async () => {
    setLoading(true);
    try {
      const userData = getUserData();
      if (userData) {
        setUser(userData);
        
        // Load notification preferences from user data or defaults
        setNotifications({
          pushNotifications: userData.notifications !== false,
          emailUpdates: userData.emailUpdates !== false,
          invitationAlerts: userData.invitationAlerts !== false,
          messageAlerts: userData.messageAlerts !== false,
          marketingEmails: userData.marketingEmails === true,
          weeklyDigest: userData.weeklyDigest !== false
        });

        // Load privacy settings from user data or defaults
        setPrivacy({
          profileVisibility: userData.profileVisibility || 'public',
          showEmail: userData.showEmail === true,
          showLinkedIn: userData.showLinkedIn !== false,
          allowDirectMessages: userData.allowDirectMessages !== false,
          showOnlineStatus: userData.showOnlineStatus !== false
        });
      }

      // Try to fetch fresh data from API
      const token = getAuthToken();
      if (token) {
        try {
          const baseUrl = getApiBaseUrl();
          const response = await axios.get(`${baseUrl}/auth/profile`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.data && response.data.success) {
            const apiData = response.data.data;
            setUser(apiData);
            
            // Update settings from API data
            setNotifications(prev => ({
              ...prev,
              pushNotifications: apiData.notifications !== false,
              emailUpdates: apiData.emailUpdates !== false,
              invitationAlerts: apiData.invitationAlerts !== false,
              messageAlerts: apiData.messageAlerts !== false,
              marketingEmails: apiData.marketingEmails === true,
              weeklyDigest: apiData.weeklyDigest !== false
            }));

            setPrivacy(prev => ({
              ...prev,
              profileVisibility: apiData.profileVisibility || 'public',
              showEmail: apiData.showEmail === true,
              showLinkedIn: apiData.showLinkedIn !== false,
              allowDirectMessages: apiData.allowDirectMessages !== false,
              showOnlineStatus: apiData.showOnlineStatus !== false
            }));
          }
        } catch (apiError) {
          console.warn('Failed to fetch fresh account settings:', apiError.message);
        }
      }
    } catch (error) {
      console.error('Error loading account settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load account settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePasswordChange = useCallback((field) => (e) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear errors when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [passwordErrors]);

  const handleNotificationChange = useCallback((setting) => (e) => {
    setNotifications(prev => ({
      ...prev,
      [setting]: e.target.checked
    }));
  }, []);

  const handlePrivacyChange = useCallback((setting) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPrivacy(prev => ({
      ...prev,
      [setting]: value
    }));
  }, []);

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword()) return;
    
    setSaving(true);
    try {
      const token = getAuthToken();
      const baseUrl = getApiBaseUrl();
      
      const response = await axios.put(`${baseUrl}/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Password changed successfully',
          severity: 'success'
        });
        
        // Clear password form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        throw new Error(response.data.error?.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to change password',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsSave = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      const baseUrl = getApiBaseUrl();
      
      const settingsData = {
        ...notifications,
        ...privacy
      };
      
      const response = await axios.put(`${baseUrl}/auth/profile`, settingsData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Settings saved successfully',
          severity: 'success'
        });
        
        // Update localStorage
        const updatedUserData = { ...user, ...settingsData };
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        setUser(updatedUserData);
        
        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('userDataUpdated', { 
          detail: updatedUserData 
        }));
      } else {
        throw new Error(response.data.error?.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Settings save error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to save settings',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAccountDeletion = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }
    
    setSaving(true);
    try {
      const token = getAuthToken();
      const baseUrl = getApiBaseUrl();
      
      const response = await axios.delete(`${baseUrl}/auth/account`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Clear all local storage and redirect to home
        localStorage.clear();
        window.location.href = '/';
      } else {
        throw new Error(response.data.error?.message || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to delete account',
        severity: 'error'
      });
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading account settings...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Account Settings
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your account security, privacy, and notification preferences
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<SecurityIcon color="primary" />}
              title="Security"
              subheader="Change your password and manage security settings"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Current Password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange('currentPassword')}
                    error={!!passwordErrors.currentPassword}
                    helperText={passwordErrors.currentPassword}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <Button
                          onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                          {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                        </Button>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="New Password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange('newPassword')}
                    error={!!passwordErrors.newPassword}
                    helperText={passwordErrors.newPassword || 'At least 6 characters'}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <Button
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </Button>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Confirm New Password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword')}
                    error={!!passwordErrors.confirmPassword}
                    helperText={passwordErrors.confirmPassword}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <Button
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </Button>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handlePasswordSubmit}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <KeyIcon />}
                    fullWidth
                  >
                    Change Password
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<NotificationsIcon color="primary" />}
              title="Notifications"
              subheader="Control what notifications you receive"
            />
            <CardContent>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.pushNotifications}
                      onChange={handleNotificationChange('pushNotifications')}
                      color="primary"
                    />
                  }
                  label="Push notifications for new messages and invitations"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.emailUpdates}
                      onChange={handleNotificationChange('emailUpdates')}
                      color="primary"
                    />
                  }
                  label="Email updates about your profile and bookings"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.invitationAlerts}
                      onChange={handleNotificationChange('invitationAlerts')}
                      color="primary"
                    />
                  }
                  label="Coffee chat invitation alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.messageAlerts}
                      onChange={handleNotificationChange('messageAlerts')}
                      color="primary"
                    />
                  }
                  label="New message alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.weeklyDigest}
                      onChange={handleNotificationChange('weeklyDigest')}
                      color="primary"
                    />
                  }
                  label="Weekly activity digest"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.marketingEmails}
                      onChange={handleNotificationChange('marketingEmails')}
                      color="primary"
                    />
                  }
                  label="Marketing emails and product updates"
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Privacy Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<PrivacyIcon color="primary" />}
              title="Privacy"
              subheader="Control who can see your information"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Profile Visibility"
                    value={privacy.profileVisibility}
                    onChange={handlePrivacyChange('profileVisibility')}
                    fullWidth
                    variant="outlined"
                    SelectProps={{ native: true }}
                    helperText="Who can see your profile"
                  >
                    <option value="public">Public - Anyone can see</option>
                    <option value="connections">Connections only</option>
                    <option value="private">Private - Hidden from search</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={privacy.showEmail}
                          onChange={handlePrivacyChange('showEmail')}
                          color="primary"
                        />
                      }
                      label="Show email address on profile"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={privacy.showLinkedIn}
                          onChange={handlePrivacyChange('showLinkedIn')}
                          color="primary"
                        />
                      }
                      label="Show LinkedIn profile on profile"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={privacy.allowDirectMessages}
                          onChange={handlePrivacyChange('allowDirectMessages')}
                          color="primary"
                        />
                      }
                      label="Allow direct messages from anyone"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={privacy.showOnlineStatus}
                          onChange={handlePrivacyChange('showOnlineStatus')}
                          color="primary"
                        />
                      }
                      label="Show online status"
                    />
                  </FormGroup>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<EmailIcon color="primary" />}
              title="Account Actions"
              subheader="Manage your account"
            />
            <CardContent>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email Address" 
                    secondary={user?.email || 'No email set'} 
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <DeleteIcon color="error" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delete Account" 
                    secondary="Permanently delete your account and all data" 
                  />
                  <ListItemSecondaryAction>
                    <Button
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSettingsSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
          sx={{ px: 4 }}
        >
          Save All Settings
        </Button>
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle color="error">Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All your data including profile, messages, and bookings will be permanently deleted.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Type <strong>DELETE</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            error={deleteConfirmText && deleteConfirmText !== 'DELETE'}
            helperText={deleteConfirmText && deleteConfirmText !== 'DELETE' ? 'Must type DELETE exactly' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={handleAccountDeletion}
            disabled={deleteConfirmText !== 'DELETE' || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccountSettings;